// /components/:widgetId/notifications: who gets an email when a homeowner sends
// the contact form (saved with the header's Save). The email itself is built
// by the public API (110-public-api/functions/public/notify.py).
import { useMemo, useState } from "react";
import { useApi } from "../../api";
import { EMAIL_CONTENTS, MAX_EMAILS, NotifyEmailsEditor, SOLAR_EMAIL_CONTENTS, useRecipients } from "../../components/NotifyEmailsEditor";
import { useSaveBar, useTabDraft, useWidget } from "./WidgetLayout";


export function NotificationsPage() {
  const { widget, setWidget } = useWidget();
  const api = useApi();
  const saved = useMemo(() => widget.notifyEmails ?? [], [widget.notifyEmails]);
  // Kept by the layout, so unsaved edits survive switching tabs
  const [emails, setEmails, discardDraft] = useTabDraft<string[]>("notifications", saved);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);
  // Which addresses are confirmed (enquiries only go to those)
  const recipients = useRecipients(emails);


  const dirty = JSON.stringify(emails) !== JSON.stringify(saved);
  useSaveBar({
    dirty,
    saving,
    blocked: false,
    onSave: save,
    onDiscard: () => {
      setEmails(saved);
      setMessage(null);
    },
  });



  async function save() {
    setSaving(true);
    setMessage(null);
    try {
      setWidget(await api.updateWidget(widget.widgetId, { notifyEmails: emails }));
      discardDraft();
      setMessage({ tone: "success", text: "Notification emails saved." });
    } catch (e) {
      setMessage({ tone: "danger", text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="install">
      {message && <div className={`flash flash-${message.tone}`}>{message.text}</div>}

      <section className="card">
        <h2>Send new enquiries to</h2>
        <p className="card-sub">
          Every time a homeowner sends their details, we email them straight to these addresses. Up to {MAX_EMAILS} per component. We
          don't keep a copy: the email is the only record, so use an inbox your team checks.
        </p>
        <p className="card-sub">
          New addresses get an email asking them to confirm, and receive nothing until they do. Your own sign-in address, and any address
          you've confirmed before, is ready straight away.
        </p>
        <NotifyEmailsEditor
          recipients={recipients}
          emails={emails}
          onChange={(next) => {
            setEmails(next);
            setMessage(null);
          }}
        />
      </section>

      <section className="card">
        <h2>What the email contains</h2>
        <ul className="notify-contents">
          {/* Solar enquiries carry the roof, system and price instead of the EPC and heat loss */}
          {(widget.product === "solar" ? SOLAR_EMAIL_CONTENTS : EMAIL_CONTENTS).map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}
